import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Loader2, Image as ImageIcon, Type } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { STORY_GRADIENTS } from "@/lib/storyConfig";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import StoryViewer from "@/components/StoryViewer";
import StoryComposer from "@/components/StoryComposer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function StoryThumb({ story }) {
  if (story.kind === "text") {
    return (
      <div className="w-full h-full flex items-center justify-center p-1" style={{ background: STORY_GRADIENTS[story.background] || STORY_GRADIENTS.grad1 }}>
        <span className="text-white text-[9px] font-bold text-center leading-tight line-clamp-3">{story.text}</span>
      </div>
    );
  }
  return <img src={mediaUrl(story.image_url)} alt="" className="w-full h-full object-cover" />;
}

export default function StoriesBar() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const { data: groups = [] } = useQuery({
    queryKey: ["stories"],
    queryFn: async () => (await api.get("/stories")).data,
  });

  const addPhotoStory = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/stories", { image_url: data.url });
      qc.invalidateQueries({ queryKey: ["stories"] });
      toast.success("Story added! It disappears in 24h.");
    } catch {
      toast.error("Could not add story");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <div className="bg-card border border-border rounded-2xl p-3">
        <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="add-story-btn" className="shrink-0 w-[86px] flex flex-col items-center gap-1.5 group">
                <div className="relative w-16 h-16 rounded-full flex items-center justify-center bg-secondary border-2 border-dashed border-[#6C0BA9]/50 group-hover:border-[#6C0BA9] transition-colors">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin text-[#C084FC]" /> : (
                    <>
                      <Avatar user={user} size={56} />
                      <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-[#6C0BA9] border-2 border-card flex items-center justify-center">
                        <Plus className="w-3.5 h-3.5 text-white" />
                      </span>
                    </>
                  )}
                </div>
                <span className="text-xs font-medium text-muted-foreground truncate w-full text-center">Your story</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-popover border-border">
              <DropdownMenuItem data-testid="add-photo-story" onClick={() => fileRef.current?.click()} className="cursor-pointer gap-2"><ImageIcon className="w-4 h-4 text-[#C084FC]" />Photo Story</DropdownMenuItem>
              <DropdownMenuItem data-testid="add-text-story" onClick={() => setComposerOpen(true)} className="cursor-pointer gap-2"><Type className="w-4 h-4 text-[#C084FC]" />Text Story</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={addPhotoStory} data-testid="story-file-input" />

          {groups.map((g, i) => (
            <button
              key={g.user.user_id}
              data-testid={`story-group-${g.user.user_id}`}
              onClick={() => setViewerIndex(i)}
              className="shrink-0 w-[86px] flex flex-col items-center gap-1.5"
            >
              <div className={`w-16 h-16 rounded-full p-[3px] ${g.has_unseen ? "bg-gradient-to-br from-[#6C0BA9] via-[#E024A3] to-[#C084FC]" : "bg-border"}`}>
                <div className="w-full h-full rounded-full overflow-hidden border-2 border-card bg-card">
                  <StoryThumb story={g.stories[0]} />
                </div>
              </div>
              <span className="text-xs font-medium truncate w-full text-center">{g.user.user_id === user.user_id ? "You" : g.user.name?.split(" ")[0]}</span>
            </button>
          ))}
        </div>
      </div>

      {viewerIndex !== null && (
        <StoryViewer
          groups={groups}
          startIndex={viewerIndex}
          onClose={() => { setViewerIndex(null); qc.invalidateQueries({ queryKey: ["stories"] }); }}
        />
      )}
      <StoryComposer open={composerOpen} onClose={() => setComposerOpen(false)} />
    </>
  );
}
